import React from 'react';
import {Alert, Box, CircularProgress, Typography} from '@mui/material';
import ReadonlyProseMirrorRenderer from '../../products/reportEditorPage/leftPanel/components/ReadonlyProseMirrorRenderer';
import {getIirLatestBody} from '../collectionTaskApi';
import {isSubstantivePmJson} from '../pmJsonContent';

function resolveIirBody(stagedPmJson, fetched) {
    const staged = stagedPmJson && isSubstantivePmJson(stagedPmJson)
        ? {pmJson: stagedPmJson}
        : null;
    const persisted = fetched?.pmJson && isSubstantivePmJson(fetched.pmJson) ? fetched : null;
    return persisted || staged || fetched || null;
}

/**
 * Read-only IIR body with NER/EL entity mentions (P11 / MINSKY-16308).
 *
 * Renders the latest persisted ProseMirror snapshot of an IIR report through the
 * same renderer the Data Items viewer uses, so `wikidataEntity` marks behave with
 * FINTEL parity: linked entities are blue dashed-underline clickable spans (hover
 * card + entity dialog), unlinked mentions are yellow-underlined.
 *
 * Intended consumers:
 *  - IirEditorPage when the IIR is read-only (submitted/approved);
 *  - P10's IIR viewer modal — embed with just a reportId.
 *
 * The snapshot is fresh at the times mentions exist: submit forces a persist, and
 * the submit-time NER pipeline persists again after applying marks.
 */
export default function IirBodyRenderer({reportId, stagedPmJson = null, sx}) {
    const hasStaged = Boolean(stagedPmJson);
    const [state, setState] = React.useState(() => ({
        loading: !hasStaged,
        body: hasStaged ? {pmJson: stagedPmJson} : null,
        error: null,
    }));

    React.useEffect(() => {
        if (!reportId) return undefined;
        let cancelled = false;
        setState({
            loading: !hasStaged,
            body: hasStaged ? {pmJson: stagedPmJson} : null,
            error: null,
        });
        getIirLatestBody(reportId)
            .then((body) => {
                if (cancelled) return;
                setState({
                    loading: false,
                    body: resolveIirBody(stagedPmJson, body),
                    error: null,
                });
            })
            .catch((e) => {
                if (cancelled) return;
                setState({
                    loading: false,
                    body: resolveIirBody(stagedPmJson, null),
                    error: e?.message || 'Failed to load IIR body',
                });
            });
        return () => { cancelled = true; };
    }, [reportId, stagedPmJson]);

    if (state.loading) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24}/>
            </Box>
        );
    }
    if (state.error) {
        return <Alert severity="error" sx={{m: 2}}>{state.error}</Alert>;
    }
    if (!state.body?.pmJson || !isSubstantivePmJson(state.body.pmJson)) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                No content has been saved for this IIR yet.
            </Typography>
        );
    }
    return (
        <Box sx={{flex: 1, minHeight: 0, px: 2, py: 1, overflowY: 'auto', ...sx}}>
            <ReadonlyProseMirrorRenderer content={state.body.pmJson}/>
        </Box>
    );
}
